import { Injectable, signal, WritableSignal } from '@angular/core';
import { Router } from '@angular/router';
import { saveAs } from 'file-saver';
import { ApiService } from 'src/app/core/services/api.service';
import { ToastService } from 'src/app/core/services/toast.service';
import { BelenderWidgetResponse } from 'src/app/models/BelenderWidgetResponse';
import { CancelRequest } from 'src/app/models/CancelRequest';
import { CancelResponse } from 'src/app/models/CancelResponse';
import { IRPFInformation } from 'src/app/models/IRPFInformation';
import { KpisGroup } from 'src/app/models/KpisGroup';
import { ODDetailResponse } from 'src/app/models/OpenData.ODDetailResponse';
import { PersonQueryStatusResponse } from 'src/app/models/PersonQueryStatusResponse';
import { IrpfTableService } from 'src/app/shared/components/irpf-table/irpf-table.service';
import { DOCUMENT_NAMES_TO_REMOVE } from 'src/app/shared/constants/innovation-documents-constants';
import { UtilsService } from 'src/app/utils/utils.service';
import { environment } from 'src/environments/environment';

@Injectable({
    providedIn: 'root'
})
export class InnovationRequestDetailService {

    isLoading = false;
    request: ODDetailResponse;
    queryId: string;
    lastQueryHour: Date;
    isHistorySidebarVisible = false;

    documentNames: string[] = [];
    kpis: WritableSignal<KpisGroup[]> = signal([]);
    protected readonly calcKpis = environment.innovationKpisCalc;

    constructor(
		private readonly apiService: ApiService,
		private readonly router: Router,
		private readonly toastService: ToastService,
        private readonly utilsService: UtilsService,
        private readonly irpfTableService: IrpfTableService
    ) { }

    getBelenderWidget(param1: string, param2: string, param3: string): Promise<BelenderWidgetResponse> {
        return this.apiService.get(`requests/belender-widget?param1=${param1}&param2=${param2}&param3=${param3}`);
    }

    getRequestFullPersonName(): string {
        if (this.request != null) {
            const firstName = this.request.personQuery?.firstName ? this.request.personQuery.firstName : '';
            const firstSurname = this.request.personQuery?.firstSurname ? this.request.personQuery.firstSurname : '';
            const secondSurname = this.request.personQuery?.secondSurname ? this.request.personQuery.secondSurname : '';
            return `${firstName} ${firstSurname} ${secondSurname}`.trim();
        }
        return null;
    }

    async saveDocument(url: string, fileName: string): Promise<void> {
        const file = await this.apiService.postBlob(`files/amazon-file`, { url: url, fileName: fileName });
        saveAs(file, fileName);
    }

    saveJSON(json: object, fileName: string): void {
        // Convert the object to a JSON string
        const jsonString = JSON.stringify(json);
        // Create a Blob from the JSON string
        const blob = new Blob([jsonString], { type: 'application/json' });
        saveAs(blob, fileName);
    }

    async getRequestFromUrl(withoutGuard = false): Promise<void> {
        this.queryId = this.validateURLAndGetRequestId(this.router.url);
        if (this.queryId) {
            await this.refreshRequest(withoutGuard);
        } else {
            this.toastService.showError('TOAST.ERROR', 'REQUEST_DETAIL.NO_QUERY_ID', true);
        };
    }

    async refreshRequest(withoutGuard: boolean): Promise<void> {
        try {
            this.isLoading = true;
            await this.setRequest(withoutGuard);
            this.setDocumentNames();
        } catch {
            this.toastService.showError('TOAST.ERROR', 'REQUEST_DETAIL.ERROR_NOT_FOUND', true);
        } finally {
            this.isLoading = false;
        }
    }

    private setDocumentNames(): void {
        this.documentNames = [];

        for (const key in this.request.documents) {
            if (!DOCUMENT_NAMES_TO_REMOVE.includes(key)) {
                this.documentNames.push(key);
            }
        }

        for (const key in this.request.data) {
            if (!this.documentNames.includes(key) && !DOCUMENT_NAMES_TO_REMOVE.includes(key)) {
                this.documentNames.push(key);
            }
        }
    }

    private async setConsentUrl(): Promise<void> {
        if (!this.request.personQuery.consentUrlBoxed) {
            const req = { queryId: this.queryId };
            const res = await this.apiService.post<PersonQueryStatusResponse>('innovation/person-query-status', req);
            this.request.personQuery.consentUrl = res.consentUrl;
            this.request.personQuery.consentUrlBoxed = res.consentUrlBoxed ? res.consentUrlBoxed : this.utilsService.convertConsentUrlToConsentUrlBoxed(res.consentUrl);
        }
    }

    private async setKpis(): Promise<void> {
        this.kpis.set([]);
        const response = await this.apiService.post<KpisGroup[]>(`innovation/kpis`, this.request);
        this.kpis.set(response);
    }

    cancelRequest(cancelRequest: CancelRequest): Promise<CancelResponse> {
        return this.apiService.post<CancelResponse>('innovation/delete-request', cancelRequest);
    }

    private validateURLAndGetRequestId(url: string): string | null {
        const regex = /request-detail\/([0-9]{14}-[A-Z0-9]{18})/;
        const matches = url.match(regex);
        if (matches && matches[1]) {
            return matches[1];
        } else {
            return null;
        }
    }

    private async setRequest(withoutGuard: boolean) {
        if (withoutGuard) {
            this.request = await this.apiService.get<ODDetailResponse>(`innovation/request-detail?queryId=${this.queryId}`);
        }

        if (this.request.data['Score']) {
            this.request.documents["Score"] = "";
        }

        if (!environment.production && !this.request.data['Calculos']) {
            this.request.data['Calculos'] = {};
        }

        const promises = [
            this.recalculateODIncomeTemporal(),
            this.recalculateBillingTemporal(),
            this.setConsentUrl(),
        ];

        if (this.calcKpis) {
            promises.push(this.setKpis());
        }

        if (this.request.personQuery.personType === '01') {
            promises.push(this.getIRPFInformation());
        }

        await Promise.all(promises);

        this.lastQueryHour = new Date();
    }

    private async recalculateODIncomeTemporal(): Promise<void> {
        if (this.request.data['Calculos']) {
            const odIncome = await this.apiService.post<number>('innovation/od-income', this.request);
            this.request.data['Calculos']['OD_Ingresos'] = odIncome;
        }
    }

    private async recalculateBillingTemporal(): Promise<void> {
        if (this.request.data['Calculos']) {
            const billing = await this.apiService.post<number>('innovation/billing', this.request);
            this.request.data['Calculos']['Facturacion'] = billing;
        }
    }

    showDocumentDetails(name: string): boolean {
        return !(name === 'Score' && this.request.data['Score'] === null);
    }

    private async getIRPFInformation(): Promise<void> {
        this.irpfTableService.reset();

        if (!this.request.data || !Object.keys(this.request.data).some(k => k.startsWith('100'))) return;

        try {
            const response = await this.apiService.post<IRPFInformation>('innovation/irpf-information', this.request);
            this.irpfTableService.init(this.queryId, response);
        } catch (error) {
            console.log(error);
        }
    }
}
